import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import PageNavigation from '../../components/PageNavigation/PageNavigation'
import './Welcome.css'

const Welcome = () => {
  const [activeSection, setActiveSection] = useState('overview')

  const sections = [
    { id: 'overview', title: 'What is Aedify.AI?' },
    { id: 'organization', title: 'How this guide is organized' },
    { id: 'quick-start', title: 'Quick start' },
    { id: 'help', title: 'Need help?' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`welcome-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="welcome-page">
      <div className="welcome-content-wrapper">
        <div className="welcome-main-content">
      <h1>Welcome to Aedify.AI</h1>
      <p className="intro">
        Aedify.AI accelerates application deployment and infrastructure management with a suite of 
        cloud-native tools purpose-built for modern development teams. This documentation helps you 
        deploy, manage, and scale your applications with confidence.
      </p>

          <section id="welcome-overview" className="welcome-section">
        <h2>What is Aedify.AI?</h2>
        <p>
          Aedify.AI unifies application deployment, infrastructure management, and cloud services 
          into a single platform. Whether you are deploying web applications, managing databases, 
          or running AI inference workloads, Aedify.AI simplifies your development workflow and 
          shortens deployment timelines from hours to minutes.
        </p>
      </section>

          <section id="welcome-organization" className="welcome-section">
        <h2>How this guide is organized</h2>
        <ul>
          <li>
            <strong>Quick Start</strong> shows you how to create your first project, deploy an application, 
            and begin building.
          </li>
          <li>
                <strong>Platform Capabilities</strong> dive into the core modules: {' '}
                <Link to="/platform-capabilities/apps">Apps</Link>,{' '}
                <Link to="/platform-capabilities/registry">Registry</Link>,{' '}
                <Link to="/platform-capabilities/database">Database</Link>, and{' '}
                <Link to="/platform-capabilities/vps">VPS</Link>.
              </li>
              <li>
                <strong>Account</strong> information covers <Link to="/account/billing">Billing</Link> and{' '}
                <Link to="/account/usage">Usage & Alerts</Link> to help you manage your account and monitor resource consumption.
          </li>
          <li>
            <strong>Resources</strong> collect FAQs, support contacts, and additional onboarding materials.
          </li>
        </ul>
      </section>

          <section id="welcome-quick-start" className="welcome-section">
        <h2>Quick start</h2>
        <ol>
          <li>
                Sign up for an account at <a href="https://aedify.ai" target="_blank" rel="noopener noreferrer">Aedify.AI</a> {' '}
            or log in to your existing account.
          </li>
          <li>
            Follow the <Link to="/quick-start">Quick Start guide</Link> to create your first project 
            and deploy an application.
          </li>
          <li>
            Explore the <Link to="/platform-capabilities/apps">Apps documentation</Link> to learn 
            about deploying from GitHub or container images.
          </li>
          <li>
                Enable supporting services — Database and VPS — to deliver 
            and iterate on your application end-to-end.
          </li>
        </ol>
      </section>

          <section id="welcome-help" className="welcome-section">
        <h2>Need help?</h2>
        <p>
          If you get stuck, reach out through the in-app support, contact your account manager, 
          or email <a href="mailto:support@aedify.ai">support@aedify.ai</a>. We are happy to guide 
          you through implementation or review your deployment strategy.
        </p>
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="welcome-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#welcome-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`welcome-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default Welcome
